// Corpo do POST /api/admin/channels/wizard (Frentes & Canais). Puro, para testar.
// O ramo WooCommerce monta EXATAMENTE o corpo que o assistente montava antes do canal
// Mercado Livre existir (teste web/test/channel-wizard.test.ts trava isso: o atacado nao muda).
// O ramo "ml" manda so Client ID (config) e Client Secret (secrets); o field_schema vai
// vazio de proposito: a API aplica o padrao do ML quando adapter = ml e nenhum campo vem.
// Client ID e CNPJ padrao NAO ficam no codigo: vem de GET /api/admin/ml/defaults (settings editaveis).

export interface FieldDraft {
  key: string;
  label: string;
  required: boolean;
}

export interface WizardInput {
  frontSlug: string;
  slug: string;
  name: string;
  kind: string;
  adapter: string;
  priceType: string;
  allocations: Array<{ stock_source_id: number; allocation_pct: number }>;
  fields: FieldDraft[];
  // WooCommerce
  wcUrl: string;
  consumerKey: string;
  consumerSecret: string;
  // Mercado Livre
  mlClientId: string;
  mlClientSecret: string;
  /** CNPJ da conta ML (um canal por conta); opcional. Guardado so com 14 digitos. */
  mlCnpj?: string;
}

export function buildChannelWizardBody(i: WizardInput): Record<string, unknown> {
  const isMl = i.adapter === "ml";
  const fieldSchema = {
    version: 1,
    fields: isMl
      ? []
      : i.fields
          .filter((f) => f.key.trim())
          .map((f) => ({ key: f.key.trim(), label: f.label || f.key, type: "text", required_for_publish: f.required })),
  };
  const secrets: Record<string, string> = {};
  let config: Record<string, unknown>;
  if (isMl) {
    config = { ml_client_id: i.mlClientId.trim() };
    const cnpj = onlyDigits(i.mlCnpj ?? "");
    if (cnpj.length === 14) config.ml_cnpj = cnpj;
    if (i.mlClientSecret) secrets.client_secret = i.mlClientSecret;
  } else {
    config = i.wcUrl ? { wc_url: i.wcUrl } : {};
    if (i.consumerKey) secrets.consumer_key = i.consumerKey;
    if (i.consumerSecret) secrets.consumer_secret = i.consumerSecret;
  }
  return {
    front_slug: i.frontSlug || undefined,
    channel: {
      slug: i.slug,
      name: i.name,
      kind: i.kind,
      adapter: i.adapter,
      config,
      secrets,
      field_schema: fieldSchema,
      price_type: i.priceType || null,
    },
    allocations: i.allocations,
  };
}

/** O botao Criar do assistente ML so liga com Client ID e Client Secret preenchidos. */
export function mlWizardReady(clientId: string, clientSecret: string): boolean {
  return clientId.trim() !== "" && clientSecret.trim() !== "";
}

// ---------------------------------------------------------------------------
// Canal ML por conta (um canal por CNPJ): nome e slug nascem do CNPJ
// ---------------------------------------------------------------------------
export const onlyDigits = (s: string): string => s.replace(/\D/g, "");

/** 19685537000160 -> 19.685.537/0001-60. Menos de 14 digitos: devolve o que ha, sem inventar. */
export function formatCnpj(raw: string): string {
  const d = onlyDigits(raw);
  if (d.length !== 14) return d;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** Nome "Mercado Livre <CNPJ formatado>" e slug "ml-<so digitos>". Sem CNPJ valido (14 digitos): nome e slug simples. */
export function mlChannelNaming(rawCnpj: string): { name: string; slug: string } {
  const d = onlyDigits(rawCnpj);
  if (d.length !== 14) return { name: "Mercado Livre", slug: "ml" };
  return { name: `Mercado Livre ${formatCnpj(d)}`, slug: `ml-${d}` };
}

const norm = (s: string): string => s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

/**
 * Frente e tabela sugeridas ao escolher o tipo ML: a frente de varejo e a tabela cujo NOME/rotulo diz varejo,
 * ambos achados nos dados que a API devolveu (nenhum codigo de tabela escrito aqui; fonte unica: channels.price_type).
 * Canal irmao da mesma frente com tabela ganha da busca por rotulo. Sem achado: null (o usuario escolhe).
 */
export function pickMlDefaults(
  fronts: Array<{ slug: string; name: string }>,
  priceTypes: Array<{ price_type: string; label: string; available: boolean }>,
  siblingPriceType: (frontSlug: string) => string,
): { frontSlug: string | null; priceType: string | null } {
  const front = fronts.find((f) => norm(f.slug).includes("varejo") || norm(f.name).includes("varejo")) ?? null;
  if (!front) return { frontSlug: null, priceType: null };
  const sibling = siblingPriceType(front.slug);
  if (sibling) return { frontSlug: front.slug, priceType: sibling };
  const byLabel = priceTypes.find((p) => p.available && (norm(p.label).includes("varejo") || norm(p.price_type).includes("varejo")));
  return { frontSlug: front.slug, priceType: byLabel?.price_type ?? null };
}

// ---------------------------------------------------------------------------
// Trocar o tipo no assistente: ao entrar no ML guarda o que estava no WooCommerce; ao sair, devolve.
// ---------------------------------------------------------------------------
export interface WizardSnapshot {
  frontSlug: string;
  priceType: string;
  name: string;
  slug: string;
  nameTouched: boolean;
  slugTouched: boolean;
}

/** Quem guarda: entrou no ML (vindo de outro tipo) => snapshot atual. Quem devolve: saiu do ML => snapshot guardado. */
export function adapterSwitch(
  from: string,
  to: string,
  current: WizardSnapshot,
  saved: WizardSnapshot | null,
): { save: WizardSnapshot | null; restore: WizardSnapshot | null } {
  if (to === "ml" && from !== "ml") return { save: current, restore: null };
  if (from === "ml" && to !== "ml") return { save: null, restore: saved };
  return { save: saved, restore: null };
}
